import React, { useState, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import Highlighter from "react-highlight-words";

// UI
import { Table, Empty, Popover, Space, message, Row, Col } from "antd";
import { MoreOutlined, SearchOutlined } from "@ant-design/icons";
import {
  CardElement,
  HomePageTable,
} from "../../../components/Common/UIElements";
import { Button, ButtonLink } from "../../../components/Common/Button/index";
import { Input } from "../../../components/Common/Input/index";
import CustomerDataCard from "../../../components/Onboarding/CustomerDataCard/CustomerDataCard";
import LoadingComponent from "../../../components/Common/LoadingComponent";
import {
  ColoredTextTag,
  TEXT_TAG_COLORS,
} from "../../../components/Common/ColoredTextTag/ColoredTextTag";

// processing
import useQuery from "../../../hooks/url.hooks";
import { tableStringSorter } from "../../../utils/table";
import useTableSearch from "../../../hooks/table-search.hooks";
import { useGetPMS } from "../../../hooks/pms.hooks";
import {
  RECORD_OPERATIONS,
  ONBOARDING_PROPERTY_STATUS,
} from "../../../utils/onboarding-helper";
import {
  useRequestValidationProperty,
  useRemoveProperties,
  useGetPropertiesByCustomer,
  useApproveSingleProperty,
} from "../../../hooks/Onboarding/onboarding-properties.hooks";
import { useActivateOnboardingUser } from "../../../hooks/Onboarding/onboarding-users.hooks";

// set table height according to window
let tableHeight = 500;
if (window.innerWidth > 700) {
  tableHeight = window.innerHeight - 375;
}

const OptionsContent = (props) => {
  const {
    singlePropertyActivateAndApprove,
    propertyDetailsValidate,
    property,
  } = props;
  const { _id, status, customer_id } = property;
  const history = useHistory();
  return (
    <Space direction="vertical" size={0}>
      <Button
        className="option-button"
        onClick={() => {
          singlePropertyActivateAndApprove(property);
        }}
        disabled={status === ONBOARDING_PROPERTY_STATUS.APPROVED}
      >
        Approve
      </Button>
      <Button
        className="option-button"
        onClick={() => {
          propertyDetailsValidate([property]);
        }}
        disabled={status === ONBOARDING_PROPERTY_STATUS.APPROVED}
      >
        Request Validation
      </Button>
      <Button
        className="option-button"
        onClick={() => {
          history.push(
            `/onboarding/corp-contact/properties/edit?property=${_id}`,
          );
        }}
        disabled={status === ONBOARDING_PROPERTY_STATUS.PENDING_APPROVAL}
      >
        Edit Property
      </Button>
      <Button
        className="option-button"
        onClick={() => {
          history.push(
            `/onboarding/corp-contact/property/users?property=${_id}&customer=${customer_id}`,
          );
        }}
      >
        Manage Users
      </Button>
      <Button
        className="option-button"
        onClick={() => {
          // deactivateCustomer(props);
        }}
        disabled
      >
        Delete
      </Button>
    </Space>
  );
};

const CorporateContactDashboard = (props) => {
  const { customer, corporateContact } = props;

  const history = useHistory();

  const query = useQuery();
  const msg = query.get("msg");

  useEffect(() => {
    if (msg) message.success(msg);
  }, [msg]);

  const [selectedList, setSelectedList] = useState([]);
  const [currentDetails, setCurrentDetails] = useState();

  const {
    isSuccess: isSuccessCusProperties,
    data: apiPropertiesData,
    refetch: refetchApiPropertiesData,
  } = useGetPropertiesByCustomer(customer._id, {
    enabled: !!customer._id,
  });

  const { data: pmsData } = useGetPMS();

  // render component for hilightable search text
  const searchHilighter = (dataIndex, customRender) => ({
    render: (text) => {
      return searchedColumns.includes(dataIndex) ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: "#ffc069",
            padding: 0,
          }}
          searchWords={searchedWords}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : customRender ? (
        customRender(text)
      ) : (
        text
      );
    },
  });

  // table columns
  const columns = [
    {
      title: "Property Name",
      dataIndex: "property_name",
      key: "property_name",
      width: "200px",
      sorter: tableStringSorter("property_name"),
      fixed: "left",
      ...searchHilighter("property_name"),
    },
    {
      title: "Property Code",
      dataIndex: "property_code",
      key: "property_code",
      width: "200px",
      sorter: tableStringSorter("property_code"),
      ...searchHilighter("property_code"),
    },
    {
      title: "Property Brand",
      dataIndex: "brand",
      key: "brand",
      width: "200px",
      sorter: tableStringSorter("brand"),
      ...searchHilighter("brand"),
    },
    {
      title: "PMS",
      dataIndex: "pms_name",
      key: "pms_name",
      width: "200px",
      sorter: tableStringSorter("pms_name"),
      ...searchHilighter("pms_name"),
    },
    {
      title: "POS",
      dataIndex: "pos",
      key: "pos",
      width: "200px",
      sorter: tableStringSorter("pos"),
      ...searchHilighter("pos"),
    },
    {
      title: "Time Zone",
      dataIndex: "property_time_zone",
      key: "property_time_zone",
      width: "200px",
      sorter: tableStringSorter("property_time_zone"),
      ...searchHilighter("property_time_zone"),
    },
    {
      title: "Property Contact First Name",
      dataIndex: "property_contact_first_name",
      key: "property_contact_first_name",
      width: "200px",
      sorter: tableStringSorter("property_contact_first_name"),
      ...searchHilighter("property_contact_first_name"),
    },
    {
      title: "Property Contact Last Name",
      dataIndex: "property_contact_last_name",
      key: "property_contact_last_name",
      width: "200px",
      sorter: tableStringSorter("property_contact_last_name"),
      ...searchHilighter("property_contact_last_name"),
    },
    {
      title: "Property Contact Email",
      dataIndex: "property_contact_email",
      key: "property_contact_email",
      width: "200px",
      sorter: tableStringSorter("property_contact_email"),
      ...searchHilighter("property_contact_email"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: "150px",
      sorter: tableStringSorter("status"),
      ...searchHilighter("status", (status) => {
        const textColor =
          status === ONBOARDING_PROPERTY_STATUS.APPROVED
            ? TEXT_TAG_COLORS.SUCCESS
            : status === ONBOARDING_PROPERTY_STATUS.PENDING_APPROVAL
            ? TEXT_TAG_COLORS.WARN
            : "";

        return <ColoredTextTag text={status} color={textColor} />;
      }),
      fixed: "right",
    },
    {
      title: "   ",
      key: "options",
      dataIndex: "_id",
      width: "50px",
      render: (_id, record) => {
        return (
          <span className="more-options-container">
            <Popover
              content={
                <OptionsContent
                  property={record}
                  propertyDetailsValidate={propertyDetailsValidate}
                  singlePropertyActivateAndApprove={
                    singlePropertyActivateAndApprove
                  }
                />
              }
              placement="left"
              trigger="click"
              overlayClassName="more-options"
            >
              <MoreOutlined />
            </Popover>
          </span>
        );
      },
      fixed: "right",
    },
  ];

  // columns that could be searched
  const searchableColumns = columns.map((col) => col.dataIndex);
  let propertiesData =
    apiPropertiesData && pmsData
      ? apiPropertiesData?.map((property) => {
          const baseProperty = { ...property };
          const propContact = baseProperty.property_contact_id;
          const pms = pmsData.filter((pms) => pms._id === property.pms)[0];

          return {
            _id: property._id,
            property_name: property.property_name,
            property_code: property.property_code,
            brand: property.brand,
            pms: property.pms,
            pms_name: pms?.pms_name,
            pos: property.pos,
            property_time_zone: property.property_time_zone,
            property_contact_first_name: propContact?.first_name,
            property_contact_last_name: propContact?.last_name,
            property_contact_email: propContact?.email,
            customer_id: property.customer_id,
            property_contact_id: propContact?._id,
            status: property.status,
          };
        })
      : [];

  // custom hook for external multi column table data searching
  const {
    filteredTableData,
    searchedColumns,
    searchedWords,
    updateSearchMeta,
    searchMeta,
  } = useTableSearch({
    searchableColumns,
    allData: propertiesData,
  });

  if (Object.keys(searchMeta).length > 0 && filteredTableData) {
    propertiesData = [...filteredTableData];
  }

  const rowSelection = {
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedList(selectedRows);
    },
  };

  const formatProperties = ({ propertyList, status }) => {
    return propertyList.map((property) => {
      return {
        _id: property._id,
        property_name: property.property_name,
        property_code: property.property_code,
        brand: property.brand,
        pms: property.pms,
        pos: property.pos,
        property_time_zone: property.property_time_zone,
        status,
        property_contact: {
          _id: property.property_contact_id,
          first_name: property.property_contact_first_name,
          last_name: property.property_contact_last_name,
          email: property.property_contact_email,
          property_id: property._id,
          customer_id: property.customer_id,
        },
        customer_id: property.customer_id,
      };
    });
  };

  const { mutateAsync: removePropertyList } = useRemoveProperties({
    onSuccess: () => {
      message.success("Properties successfully removed");
      refetchApiPropertiesData();
    },
    onError: (error) => {
      message.error(error.response?.data?.message ?? "Server Error");
    },
  });

  const { mutateAsync: validatePropertyList } = useRequestValidationProperty({
    onSuccess: () => {
      message.success("Validation request successfully sent");
      refetchApiPropertiesData();
    },
    onError: (error) => {
      message.error(error.response?.data?.message ?? "Server Error");
    },
  });
  const { mutateAsync: activatePropertyContact } = useActivateOnboardingUser({
    onSuccess: () => {
      message.success("Activation request successfully sent");
    },
    onError: (error) => {
      message.error(error.response?.data?.message ?? "Server Error");
    },
  });

  // Approve Single Property hooks
  const { mutate: approveSingleProperty } = useApproveSingleProperty({
    onSuccess: () => {
      message.success("Property successfully approved");
      refetchApiPropertiesData();
    },
    onError: (error) => {
      message.error(error.response?.data?.message ?? "Server Error");
    },
  });

  // Single Property Activate and Approve
  const singlePropertyActivateAndApprove = async (data) => {
    try {
      const activateProp = await activatePropertyContact({
        id: data?.property_contact_id,
      });
      if (activateProp !== undefined) {
        const approveProp = await approveSingleProperty(data?._id);
      }
    } catch (error) {
      message.error(error.response?.data?.message);
    }
  };
  // Multiple Property Activate and Approve
  const propertyListActivateAndApprove = async (propertyList) => {
    try {
      if (!propertyList) {
        propertyList = [...selectedList];
      }
      propertyList = formatProperties({
        propertyList,
        status: ONBOARDING_PROPERTY_STATUS.PENDING_APPROVAL,
      });
      if (propertyList.length === 0) {
        message.error("Please select at least one property");
        return;
      }

      propertyList.forEach(async (propertyElement) => {
        let activateProp = await activatePropertyContact({
          id: propertyElement?.property_contact?._id,
        });

        if (activateProp !== undefined) {
          const approveProp = await approveSingleProperty(propertyElement?._id);
        } else {
          return false;
        }
      });
    } catch (error) {
      console.log(error, "error");
      message.error(error.response?.data?.message);
    }
  };
  const propertyDetailsValidate = (propertyList) => {
    if (!propertyList) {
      propertyList = [...selectedList];
    }

    propertyList = formatProperties({
      propertyList,
      status: ONBOARDING_PROPERTY_STATUS.PENDING_APPROVAL,
    });

    if (propertyList.length === 0) {
      message.error("Please select at least one property");
      return;
    }
    validatePropertyList({ propertyList });
  };

  const propertyDetailsRemove = () => {
    const propertyList = [...selectedList];

    if (propertyList.length === 0) {
      message.error("Please select at least one property");
    }

    const propertiesToRemove = [];

    propertyList.forEach((item) => {
      if (
        item.status === ONBOARDING_PROPERTY_STATUS.APPROVED ||
        item.status === ONBOARDING_PROPERTY_STATUS.PENDING_APPROVAL ||
        item.status === ONBOARDING_PROPERTY_STATUS.PENDING_ACTIVATION
      ) {
        message.error("a related Hotel exists in SM");
      } else {
        propertiesToRemove.push(item._id);
      }
    });

    if (propertiesToRemove.length > 0) {
      removePropertyList({ propertyList: propertiesToRemove });
    }
  };

  const addProperty = () => {
    history.push(
      `/onboarding/corp-contact/properties/add?customer_id=${
        customer._id
      }&allowed_properties=${
        customer.number_of_properties
      }&uploaded_properties=${apiPropertiesData?.length || 0}`,
    );
  };
  const addPropertyButton = useMemo(() => {
    return isSuccessCusProperties &&
      customer.number_of_properties > apiPropertiesData?.length ? (
      <ButtonLink onClick={addProperty}>
        <span style={{ textDecoration: "underline" }}>Add Property</span>
      </ButtonLink>
    ) : null;
  }, [
    customer.number_of_properties,
    apiPropertiesData,
    isSuccessCusProperties,
  ]);

  return (
    <CardElement title="System Discovery">
      <Row>
        <Col span={22}>
          <h2>Hotel Management Group</h2>
        </Col>
        <Col span={2}>
          <ButtonLink
            onClick={() => {
              history.push(
                currentDetails && currentDetails === "billing-details"
                  ? `/onboarding/corp-contact/billing/${RECORD_OPERATIONS.EDIT}?customer=${customer._id}`
                  : `/onboarding/corp-contact/customer/${RECORD_OPERATIONS.EDIT}?customer=${customer._id}`,
              );
            }}
            disabled={currentDetails && currentDetails === "billing-details"}
          >
            <span style={{ textDecoration: "underline" }}>Edit</span>
          </ButtonLink>
        </Col>
        <hr />
      </Row>
      <CustomerDataCard
        customer={customer}
        corporateContact={corporateContact}
        onChange={(activeKey) => {
          setCurrentDetails(activeKey);
        }}
      />
      <Row>
        <Col span={22}>
          <h2>Property List</h2>
        </Col>
        <Col span={2}>{addPropertyButton}</Col>
        <hr />
      </Row>
      <Row style={{ marginBottom: "20px" }}>
        <Col xxl={20} xl={20} lg={20} md={20} sm={24} xs={24}>
          <div style={{ display: "inline", paddingRight: "10px" }}>
            <div className="information-container">
              <span className="information-descriptor">Bulk Actions :</span>
            </div>
            <ButtonLink
              style={{ padding: "5px" }}
              onClick={() => {
                propertyListActivateAndApprove();
              }}
            >
              <span style={{ textDecoration: "underline" }}>Approve</span>
            </ButtonLink>
            <ButtonLink
              style={{ padding: "5px" }}
              onClick={() => {
                propertyDetailsValidate();
              }}
            >
              <span style={{ textDecoration: "underline" }}>
                Request Validation
              </span>
            </ButtonLink>
            <ButtonLink
              style={{ padding: "5px" }}
              onClick={() => {
                propertyDetailsRemove();
              }}
            >
              <span style={{ textDecoration: "underline" }}>Remove</span>
            </ButtonLink>
          </div>
          <div style={{ display: "inline" }}>
            <div className="information-container">
              <span className="information-descriptor">
                Allowed Properties : &nbsp;
              </span>
              <span className="information-display">
                {customer.number_of_properties}
              </span>
            </div>
            <div className="information-container">
              <span className="information-descriptor">
                Approved Properties : &nbsp;
              </span>
              <span className="information-display">
                {
                  apiPropertiesData?.filter(
                    (property) =>
                      property.status === ONBOARDING_PROPERTY_STATUS.APPROVED,
                  )?.length
                }
              </span>
            </div>
            <div className="information-container">
              <span className="information-descriptor">
                Uploaded Properties : &nbsp;
              </span>
              <span className="information-display">
                {apiPropertiesData?.length || 0}
              </span>
            </div>
            <div className="information-container">
              <span className="information-descriptor">
                Pending Approval Properties : &nbsp;
              </span>
              <span className="information-display">
                {
                  apiPropertiesData?.filter(
                    (property) =>
                      property.status ===
                      ONBOARDING_PROPERTY_STATUS.PENDING_APPROVAL,
                  )?.length
                }
              </span>
            </div>
          </div>
        </Col>
        <Col xxl={4} xl={4} lg={4} md={4} sm={24} xs={24}>
          <Input
            name="searchAll"
            placeholder="Search"
            className="onboarding-toolbar-input"
            prefix={<SearchOutlined />}
            onChange={(e) => {
              updateSearchMeta(e.target.value, "searchAll");
            }}
            allowClear
          />
        </Col>
      </Row>
      <Row>
        <div style={{ width: "100%" }}>
          {apiPropertiesData === undefined ? (
            <Empty description="Please select your filters and click the Go button" />
          ) : (
            <div>
              {apiPropertiesData && apiPropertiesData.length > 0 ? (
                <HomePageTable>
                  <Table
                    className="onboarding-properties-table"
                    columns={columns}
                    dataSource={propertiesData}
                    rowKey="_id"
                    scroll={{ x: 1100, y: tableHeight }}
                    pagination={{
                      defaultPageSize: 10,
                      showSizeChanger: true,
                      pageSizeOptions: ["10", "25", "50", "100"],
                    }}
                    locale={{
                      emptyText: !isSuccessCusProperties ? (
                        <div className="spinner-table">
                          <LoadingComponent />
                        </div>
                      ) : (
                        <Empty
                          description="No such property found"
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                        />
                      ),
                    }}
                    rowSelection={{
                      type: "checkbox",

                      ...rowSelection,
                    }}
                  />
                </HomePageTable>
              ) : (
                <Empty description="No Customer found" />
              )}
            </div>
          )}
        </div>
      </Row>
    </CardElement>
  );
};

export default CorporateContactDashboard;
